/**
 * Collection View Toggle
 * Handles switching between grid and list views in collection pages
 * @author Studio Almond
 * @version 1.0.0
 */

class CollectionViewToggle {
  constructor() {
    this.STORAGE_KEY = 'acme-collection-view-preference';
    this.DEFAULT_VIEW = 'trade'; // Wide view by default
    this.ANIMATION_DURATION = 300;

    this.elements = {
      toggle: null,
      buttons: [],
      containers: [],
    };

    this.currentView = this.DEFAULT_VIEW;
    this.isAnimating = false;

    this.init();
  }

  /**
   * Initialize the view toggle functionality
   */
  init() {
    console.log('init toggle...')
    try {
      this.cacheElements();
      this.bindEvents();
      this.loadSavedPreference();
      this.setInitialState();
    } catch (error) {
      console.error('CollectionViewToggle: Initialization failed', error);
    }
  }

  /**
   * Cache DOM elements for performance
   */
  cacheElements() {
    console.log(this.elements.toggle)
    this.elements.toggle = document.querySelector('[data-view-toggle]');
    this.elements.buttons = Array.from(document.querySelectorAll('.view-toggle__button'));
    this.elements.containers = Array.from(document.querySelectorAll('.collection-view'));

    if (!this.elements.toggle || this.elements.buttons.length === 0) {
      throw new Error('Required DOM elements not found');
    }

    // Read customer-type default from Liquid-rendered data attribute
    const domDefault = this.elements.toggle.dataset.defaultView;
    if (domDefault) {
        this.DEFAULT_VIEW = domDefault;
        this.currentView = domDefault;
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    this.elements.buttons.forEach((button) => {
      button.addEventListener('click', this.handleViewChange.bind(this));
    });

    // Handle keyboard navigation
    this.elements.toggle.addEventListener('keydown', this.handleKeydown.bind(this));

    // Handle visibility change to update aria attributes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * Handle view change when button is clicked
   * @param {Event} event - Click event
   */
  handleViewChange(event) {
    if (this.isAnimating) return;

    const button = event.currentTarget;
    const targetView = button.getAttribute('data-view');

    if (targetView === this.currentView) return;

    this.switchView(targetView);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeydown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const currentIndex = this.elements.buttons.findIndex((btn) =>
        btn.classList.contains('view-toggle__button--active'),
      );

      let nextIndex;
      if (event.key === 'ArrowLeft') {
        nextIndex = currentIndex === 0 ? this.elements.buttons.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex === this.elements.buttons.length - 1 ? 0 : currentIndex + 1;
      }

      const targetView = this.elements.buttons[nextIndex].getAttribute('data-view');
      this.switchView(targetView);
    }
  }

  /**
   * Handle visibility change for accessibility
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.elements.buttons.forEach((button) => {
        button.setAttribute('tabindex', '-1');
      });
    } else {
      this.updateAriaAttributes();
    }
  }

  /**
   * Switch between views with animation
   * @param {string} targetView - Target view identifier
   */
  async switchView(targetView) {
    if (this.isAnimating || targetView === this.currentView) return;

    this.isAnimating = true;

    try {
      // Update button states immediately for better UX
      this.updateButtonStates(targetView);

      // Animate view transition
      await this.animateViewTransition(targetView);

      // Update current view and save preference
      this.currentView = targetView;
      this.savePreference(targetView);

      // Update accessibility attributes
      this.updateAriaAttributes();

      // Trigger custom event for other scripts to listen
      this.triggerCustomEvent(targetView);
    } catch (error) {
      console.error('CollectionViewToggle: View switch failed', error);
    } finally {
      this.isAnimating = false;
    }
  }

  /**
   * Animate the transition between views
   * @param {string} targetView - Target view identifier
   */
  animateViewTransition(targetView) {
    return new Promise((resolve) => {
      const currentContainer = this.elements.containers.find((container) =>
        container.classList.contains('collection-view--active'),
      );

      const targetContainer = this.elements.containers.find(
        (container) => container.getAttribute('data-view') === targetView,
      );

      if (!targetContainer) {
        resolve();
        return;
      }

      // Fade out current view
      if (currentContainer) {
        currentContainer.style.opacity = '0';
      }

      // Show target view after delay
      setTimeout(() => {
        this.elements.containers.forEach((container) => {
          const isTarget = container.getAttribute('data-view') === targetView;

          container.style.display = isTarget ? 'block' : 'none';
          container.classList.toggle('collection-view--active', isTarget);

          if (isTarget) {
            // Reset and animate in
            container.style.opacity = '0';

            // Force reflow
            container.offsetHeight;

            // Animate in
            container.style.transition = `opacity ${this.ANIMATION_DURATION}ms ease`;
            container.style.opacity = '1';
          }
        });

        setTimeout(resolve, this.ANIMATION_DURATION);
      }, 150);
    });
  }

  /**
   * Update button active states
   * @param {string} activeView - Active view identifier
   */
  updateButtonStates(activeView) {
    this.elements.buttons.forEach((button) => {
      const isActive = button.getAttribute('data-view') === activeView;
      button.classList.toggle('view-toggle__button--active', isActive);
      button.setAttribute('aria-pressed', isActive.toString());
    });
  }

  /**
   * Update ARIA attributes for accessibility
   */
  updateAriaAttributes() {
    this.elements.buttons.forEach((button) => {
      const isActive = button.classList.contains('view-toggle__button--active');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  /**
   * Load saved preference from localStorage
   */
  loadSavedPreference() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved && this.isValidView(saved)) {
        this.currentView = saved;
      }
    } catch (error) {
      console.warn('CollectionViewToggle: Could not load saved preference', error);
    }
  }

  /**
   * Save preference to localStorage
   * @param {string} view - View to save
   */
  savePreference(view) {
    try {
      localStorage.setItem(this.STORAGE_KEY, view);
    } catch (error) {
      console.warn('CollectionViewToggle: Could not save preference', error);
    }
  }

  /**
   * Check if view is valid
   * @param {string} view - View to validate
   * @returns {boolean} - Is valid view
   */
  isValidView(view) {
    return this.elements.buttons.some((button) => button.getAttribute('data-view') === view);
  }

  /**
   * Set initial state based on current view
   */
  setInitialState() {
    this.updateButtonStates(this.currentView);
    this.updateAriaAttributes();

    // Set initial container visibility without animation
    this.elements.containers.forEach((container) => {
      const isActive = container.getAttribute('data-view') === this.currentView;
      container.style.display = isActive ? 'block' : 'none';
      container.classList.toggle('collection-view--active', isActive);
      container.style.opacity = '1';
      container.style.transform = 'translateY(0)';
    });
  }

  /**
   * Trigger custom event for other scripts to listen
   * @param {string} view - Current view
   */
  triggerCustomEvent(view) {
    const event = new CustomEvent('collectionViewChanged', {
      detail: { view, timestamp: Date.now() },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Public method to get current view
   * @returns {string} - Current view
   */
  getCurrentView() {
    return this.currentView;
  }

  /**
   * Public method to programmatically switch view
   * @param {string} view - Target view
   */
  setView(view) {
    if (this.isValidView(view)) {
      this.switchView(view);
    }
  }

  /**
   * Sync internal state with DOM (useful after DOM updates)
   * @param {string} view - View to sync to
   */
  syncState(view) {
    if (this.isValidView(view)) {
      this.currentView = view;
      this.updateButtonStates(view);
      this.updateAriaAttributes();
    }
  }

  /**
   * Re-initialize after DOM content replacement
   * This rebinds event listeners to new DOM elements
   */
  reinitialize() {
    try {
      // Store current view before reinitializing
      const currentView = this.currentView;

      // Remove old event listeners
      this.removeEventListeners();

      // Re-cache elements and bind new listeners
      this.cacheElements();
      this.bindEvents();

      // Restore the view state
      this.currentView = currentView;
      this.setInitialState();
    } catch (error) {
      console.error('CollectionViewToggle: Reinitialization failed', error);
      // Fallback to full initialization
      this.init();
    }
  }

  /**
   * Remove event listeners (helper for reinitialize)
   */
  removeEventListeners() {
    if (this.elements.buttons) {
      this.elements.buttons.forEach((button) => {
        button.removeEventListener('click', this.handleViewChange);
      });
    }

    if (this.elements.toggle) {
      this.elements.toggle.removeEventListener('keydown', this.handleKeydown);
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Destroy the instance and clean up
   */
  destroy() {
    this.elements.buttons.forEach((button) => {
      button.removeEventListener('click', this.handleViewChange);
    });

    this.elements.toggle?.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    this.elements = { toggle: null, buttons: [], containers: [] };
  }
}

// Make class available globally
window.CollectionViewToggle = CollectionViewToggle;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Create global instance for potential external access
  window.collectionViewToggle = new CollectionViewToggle();
});

// Handle page unload
window.addEventListener('beforeunload', () => {
  if (window.collectionViewToggle) {
    window.collectionViewToggle.destroy();
  }
});
